const ANIMATED_THUMBNAIL_SELECTOR = "[data-guide-card-animation]";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function requiredSource(value) {
  const source = String(value ?? "").trim();
  if (!source) {
    throw new TypeError("Guide card animation requires a GIF source");
  }
  return source;
}

function positiveDuration(value) {
  const duration = Number(value);
  if (!Number.isFinite(duration) || duration <= 0) {
    throw new TypeError("Guide card animation requires a positive intro duration");
  }
  return duration;
}

export function bindGuideCardAnimation(
  container,
  {
    clearTimer = globalThis.clearTimeout?.bind(globalThis),
    matchMedia = globalThis.matchMedia?.bind(globalThis),
    queueTask = globalThis.queueMicrotask?.bind(globalThis) ?? ((task) => Promise.resolve().then(task)),
    setTimer = globalThis.setTimeout?.bind(globalThis),
  } = {},
) {
  const card = container?.closest?.(".guide-card");
  const image = container?.querySelector?.("[data-animation-gif]");
  const still = container?.querySelector?.("[data-animation-still]");
  const stillContext = still?.getContext?.("2d");
  if (!card || !image || !still || !stillContext) {
    return null;
  }

  const source = requiredSource(image.getAttribute("src"));
  const introDurationMs = positiveDuration(container.dataset.animationIntroMs);
  const motionQuery = typeof matchMedia === "function"
    ? matchMedia(REDUCED_MOTION_QUERY)
    : null;
  let focused = Boolean(card.matches?.(":focus-within"));
  let hovered = Boolean(card.matches?.(":hover"));
  let introActive = !(motionQuery?.matches ?? false);
  let introTimer = null;
  let hasStill = false;
  let playing = true;

  function shouldPlay() {
    return !(motionQuery?.matches ?? false) && (introActive || hovered || focused);
  }

  function finishIntro() {
    if (!introActive) {
      return;
    }
    introActive = false;
    if (introTimer !== null) {
      clearTimer?.(introTimer);
      introTimer = null;
    }
    sync();
  }

  function scheduleIntroEnd() {
    if (!introActive || introTimer !== null) {
      return;
    }
    introTimer = setTimer?.(() => {
      introTimer = null;
      finishIntro();
    }, introDurationMs) ?? null;
  }

  function captureStill() {
    if (hasStill || image.naturalWidth <= 0 || image.naturalHeight <= 0) {
      return;
    }
    still.width = image.naturalWidth;
    still.height = image.naturalHeight;
    stillContext.drawImage(image, 0, 0, still.width, still.height);
    hasStill = true;
  }

  function onImageLoad() {
    if (!playing) {
      return;
    }
    captureStill();
    if (!shouldPlay()) {
      stop();
      return;
    }
    image.hidden = false;
    still.style.visibility = "hidden";
    scheduleIntroEnd();
  }

  function stop(waitForStill = true) {
    if (!playing) {
      return;
    }
    image.hidden = true;
    if (waitForStill && !hasStill) {
      return;
    }
    image.removeAttribute("src");
    still.style.visibility = hasStill ? "" : "hidden";
    playing = false;
  }

  function sync() {
    const active = shouldPlay();
    if (active === playing) {
      return;
    }
    if (active) {
      image.hidden = true;
      playing = true;
      image.src = source;
      if (image.complete) {
        onImageLoad();
      }
    } else {
      stop();
    }
  }

  function onPointerEnter() {
    hovered = true;
    sync();
  }

  function onPointerLeave() {
    hovered = false;
    sync();
  }

  function onFocusIn() {
    focused = true;
    sync();
  }

  function onFocusOut() {
    queueTask(() => {
      focused = Boolean(card.matches?.(":focus-within"));
      sync();
    });
  }

  function onMotionChange() {
    if (motionQuery?.matches) {
      finishIntro();
    }
    sync();
  }

  image.addEventListener("load", onImageLoad);
  card.addEventListener("pointerenter", onPointerEnter);
  card.addEventListener("pointerleave", onPointerLeave);
  card.addEventListener("focusin", onFocusIn);
  card.addEventListener("focusout", onFocusOut);
  motionQuery?.addEventListener?.("change", onMotionChange);
  if (image.complete) {
    onImageLoad();
  }
  sync();

  return Object.freeze({
    destroy() {
      introActive = false;
      if (introTimer !== null) {
        clearTimer?.(introTimer);
        introTimer = null;
      }
      image.removeEventListener("load", onImageLoad);
      card.removeEventListener("pointerenter", onPointerEnter);
      card.removeEventListener("pointerleave", onPointerLeave);
      card.removeEventListener("focusin", onFocusIn);
      card.removeEventListener("focusout", onFocusOut);
      motionQuery?.removeEventListener?.("change", onMotionChange);
      stop(false);
    },
  });
}

export function installGuideCardAnimations(root = globalThis.document) {
  if (!root?.querySelectorAll) {
    return [];
  }
  return Array.from(root.querySelectorAll(ANIMATED_THUMBNAIL_SELECTOR))
    .map((container) => bindGuideCardAnimation(container))
    .filter(Boolean);
}

function installDocumentAnimations() {
  installGuideCardAnimations(globalThis.document);
}

if (globalThis.document?.readyState === "loading") {
  globalThis.document.addEventListener("DOMContentLoaded", installDocumentAnimations, {
    once: true,
  });
} else {
  installDocumentAnimations();
}
